// 📧 Email Utilities for ACCTOO
// Provides email normalization and validation utilities

/**
 * Normalizes an email address to ensure consistent formatting
 * Firebase Auth treats emails as case-insensitive, but we normalize
 * on the client side for consistency across our system
 * 
 * @param {string} email - The email address to normalize
 * @returns {string} - Normalized email address (trimmed and lowercased)
 */
export const normalizeEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return '';
  }
  
  // Trim whitespace and convert to lowercase
  return email.trim().toLowerCase();
};

/**
 * Validates email format using a simple regex
 * 
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if email format is valid
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  
  // Basic email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates and normalizes an email address
 * 
 * @param {string} email - The email address to validate and normalize
 * @returns {object} - { valid: boolean, normalized: string, error?: string }
 */
export const validateAndNormalizeEmail = (email) => {
  const normalized = normalizeEmail(email);
  
  if (!normalized) {
    return {
      valid: false,
      normalized: '',
      error: 'Email address is required'
    };
  }
  
  if (!isValidEmail(normalized)) {
    return {
      valid: false,
      normalized,
      error: 'Please enter a valid email address'
    };
  }
  
  return {
    valid: true,
    normalized
  };
};

/**
 * Get user-friendly error message for Firebase Auth errors
 * Enhanced error messages for better user understanding
 * 
 * @param {string} errorCode - Firebase error code
 * @param {string} defaultMessage - Default message if code not found
 * @returns {string} - User-friendly error message
 */
export const getAuthErrorMessage = (errorCode, defaultMessage = 'An error occurred. Please try again.') => {
  const errorMessages = {
    // Email/Password errors
    'auth/user-not-found': 'No account found with this email address. Please check your email or sign up.',
    'auth/wrong-password': 'Incorrect password. Please try again or use "Forgot Password" to reset.',
    'auth/invalid-email': 'Invalid email address format. Please enter a valid email.',
    'auth/weak-password': 'Password is too weak. Please use at least 6 characters.',
    'auth/email-already-in-use': 'This email address is already registered. Please try signing in or use a different email.',
    'auth/email-already-exists': 'This email address is already in use. Please sign in or use "Forgot Password".',
    
    // Password requirements
    'auth/password-does-not-meet-requirements': 'Password does not meet requirements. Use at least 8 characters with uppercase, lowercase, numbers, and special characters.',
    
    // Account status errors
    'auth/user-disabled': 'This account has been disabled. Please contact support for assistance.',
    'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method. Please try signing in with your original method.',
    
    // Security errors
    'auth/too-many-requests': 'Too many failed login attempts. Please wait a few minutes and try again, or reset your password.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
    'auth/requires-recent-login': 'For security reasons, please log out and log in again to perform this action.',
    
    // Network errors
    'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
    'auth/timeout': 'The operation timed out. Please check your connection and try again.',
    
    // Invalid credentials
    'auth/invalid-credential': 'Invalid email or password. Please check your credentials and try again.',
    'auth/invalid-login-credentials': 'Invalid email or password. Please verify your login information.',
    'auth/user-token-expired': 'Your session has expired. Please sign in again.',
    'auth/invalid-user-token': 'Your session is invalid. Please sign in again.',
    
    // OTP/Phone errors
    'auth/invalid-verification-code': 'Invalid verification code. Please check and try again.',
    'auth/code-expired': 'Verification code has expired. Please request a new one.',
    'auth/invalid-phone-number': 'Invalid phone number format. Please use a valid phone number.',
    'auth/phone-number-already-exists': 'This phone number is already registered. Please sign in.',
    
    // Session errors
    'auth/null-user': 'No user is currently signed in. Please sign in to continue.',
    'auth/invalid-api-key': 'Authentication service configuration error. Please contact support.',
    
    // Permission errors
    'permission-denied': 'You do not have permission to access this resource. Please contact an administrator.',
    'auth/unauthorized-domain': 'This domain is not authorized to use this authentication method.',
  };
  
  return errorMessages[errorCode] || defaultMessage;
};

/**
 * Format password requirements error message
 * 
 * @param {string} errorMessage - Original Firebase error message
 * @returns {string} - Formatted, user-friendly error message
 */
export const formatPasswordError = (errorMessage) => {
  if (!errorMessage) return 'Password requirements not met.';
  
  const requirements = [];
  
  if (errorMessage.includes('lower case character') || errorMessage.includes('lowercase')) {
    requirements.push('a lowercase letter (a-z)');
  }
  if (errorMessage.includes('upper case character') || errorMessage.includes('uppercase')) {
    requirements.push('an uppercase letter (A-Z)');
  }
  if (errorMessage.includes('numeric character') || errorMessage.includes('number')) {
    requirements.push('a number (0-9)');
  }
  if (errorMessage.includes('non-alphanumeric') || errorMessage.includes('special character')) {
    requirements.push('a special character (!@#$%^&*)');
  }
  if (errorMessage.includes('at least') && errorMessage.match(/\d+/)) {
    const length = errorMessage.match(/\d+/)[0];
    requirements.push(`at least ${length} characters`);
  }
  
  if (requirements.length > 0) {
    return `Password must include ${requirements.join(', ')}.`;
  }
  
  return 'Password is too weak. Please choose a stronger password with at least 8 characters.';
};

export default {
  normalizeEmail,
  isValidEmail,
  validateAndNormalizeEmail,
  getAuthErrorMessage,
  formatPasswordError
};








































